import React, { useCallback, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { bankTransferAutoConfirmAdminApiService } from "../../../infrastructure/api/admin/bank-transfer-auto-confirm-admin-api-service";
import type {
  AutoConfirmRevenue,
  AutoConfirmRevenueCompany,
} from "../../../domain/types/bank-transfer-auto-confirm";

/**
 * 무통장입금 자동확인으로 **얼마를 벌고 얼마를 쓰는가.**
 *
 * 회사의 첫 유료 기능인데 그걸 한 번에 보는 곳이 없었다. 토스 콘솔에는 받은 돈만 있고
 * 연동사에 나가는 돈이 없어 남는 돈을 알 수 없었다.
 *
 * **상단과 그래프의 기준이 다르다. 일부러 그렇다.**
 *
 * 상단은 통장 기준이다 — 받은 돈에서 지출을 뺀다. 세 숫자를 눈으로 빼도 맞아떨어져야 하는
 * 자리라 다른 기준을 섞지 않는다.
 *
 * 그래프는 연간 결제를 이용기간에 나눈 값이다. 받은 달에 몰아서 그리면 연간 계약이 들어온
 * 달만 튀고 그 뒤 11개월이 적자로 보인다 — 연동사 요금은 매달 나가기 때문이다.
 */
const MONTH_OPTIONS = [1, 6, 12, 24];

const won = (v: number) => `${v.toLocaleString()}원`;
const shortWon = (v: number) =>
  Math.abs(v) >= 10_000
    ? `${Math.round(v / 10_000).toLocaleString()}만`
    : `${v.toLocaleString()}`;

/**
 * 상태 뱃지.
 *
 * **빨간색을 쓰지 않는다.** 결제 재시도는 사고가 아니라 유예 안에서 되돌아올 수 있는
 * 상태고, 해지도 정상적인 선택이다. 여기서 붉게 칠하면 매일 보는 목록이 경고판이 된다.
 * 손대야 할 것(재시도)만 파랗게 띄우고 나머지는 회색으로 눕힌다.
 */
const STATUS_LABEL: Record<string, [string, string]> = {
  ACTIVE: ["정상", "bg-[#F5F5F5] text-[#888888]"],
  ACTIVATING: ["등록 중", "bg-[#F5F5F5] text-[#888888]"],
  PAST_DUE: ["결제 재시도", "bg-[#E8EFFF] text-[#4285F4]"],
  CANCEL_REQUESTED: ["해지", "bg-[#F5F5F5] text-[#888888]"],
};

const BankTransferRevenue: React.FC = () => {
  const [data, setData] = useState<AutoConfirmRevenue>();
  const [months, setMonths] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const rangeLabel = months === 1 ? "이번 달" : `최근 ${months}개월`;

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await bankTransferAutoConfirmAdminApiService.revenue(months));
    } catch (e) {
      setError(e instanceof Error ? e.message : "매출을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * 상단은 **선택한 구간 전체**를 더한다.
   *
   * 개월 버튼이 그래프에만 걸려 있으면 위아래가 따로 논다. 12개월을 골라 놓고
   * 상단만 이번 달이면 "이 기능으로 그동안 얼마를 벌었나"를 볼 방법이 없다.
   */
  const rows = data?.monthly ?? [];
  const sum = (pick: (row: (typeof rows)[number]) => number) =>
    rows.reduce((total, row) => total + pick(row), 0);

  const paidTotal = sum((row) => row.paidAmount);
  const costTotal = sum((row) => row.vendorCost);
  const cashMargin = paidTotal - costTotal;
  const monthlyPaid = sum((row) => row.paidByPlan.MONTHLY ?? 0);
  const annualPaid = sum((row) => row.paidByPlan.ANNUAL ?? 0);

  // 지금 이 순간의 계좌 구성. 위 합계와 달리 "현재"다
  const liveAccounts = (data?.companies ?? []).reduce(
    (n, it) => n + it.accounts,
    0,
  );
  const liveAnnual = (data?.companies ?? []).reduce(
    (n, it) => n + (it.plans.ANNUAL ?? 0),
    0,
  );
  const liveMonthly = liveAccounts - liveAnnual;

  /**
   * 월별 추이는 **실제로 받은 돈**을 그린다.
   *
   * 연간 결제를 12개월로 나눠 그리면 선은 매끈해지지만 어느 달에도 실제로 없던 금액이
   * 화면에 남는다. 통장과 대조가 안 되는 그래프가 된다.
   * 대신 월간·연간을 쌓아 **그 달이 왜 튀었는지** 보이게 한다.
   */
  const chart = (data?.monthly ?? []).map((row) => ({
    month: row.month.slice(2).replace("-", "."),
    월간: row.paidByPlan.MONTHLY ?? 0,
    연간: row.paidByPlan.ANNUAL ?? 0,
    지출: row.vendorCost,
    마진: row.paidAmount - row.vendorCost,
  }));

  /**
   * 한 달을 고르면 날짜별로 그린다. 월 막대 하나로는 아무것도 안 보인다.
   *
   * **여기에는 지출·마진이 없다.** 연동사 요금은 계좌당 월정액이라 하루로 나누면
   * 없는 숫자를 지어내는 셈이다. 일별은 "언제 얼마 들어왔나"만 답한다.
   */
  const daily = (data?.daily ?? []).map((row) => ({
    day: Number(row.date.slice(8)),
    월간: row.paidByPlan.MONTHLY ?? 0,
    연간: row.paidByPlan.ANNUAL ?? 0,
  }));

  return (
    <Pod24AdminLayout>
      <div className="text-left">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-[16px] font-bold">무통장입금</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              {MONTH_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setMonths(option)}
                  className={`rounded-md px-2.5 py-1.5 text-[12px] font-medium ${
                    months === option
                      ? "bg-[#E8EFFF] text-[#4285F4]"
                      : "text-[#888] hover:bg-[#F5F5F5]"
                  }`}
                >
                  {option}개월
                </button>
              ))}
            </div>
            <button
              onClick={() => void reload()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#D9D9D9] px-3 py-2 text-[12px] text-[#555] hover:bg-[#F5F5F5]"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />{" "}
              새로고침
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[12px] text-[#4285F4]">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat
            label={`수납액 · ${rangeLabel}`}
            value={won(paidTotal)}
            note={`월간 ${won(monthlyPaid)} · 연간 ${won(annualPaid)}`}
          />
          <Stat
            label={`연동사 지출 · ${rangeLabel}`}
            value={won(costTotal)}
            note={`이번 달 ${data?.current.billedAccounts ?? 0}계좌 · 추정`}
          />
          {/*
            통장 기준으로 맞춘다. 위 두 칸을 눈으로 빼서 확인하는 자리라
            수납액과 다른 기준을 섞으면 산수가 안 맞아 보인다.
            나눠 본 값은 바로 아래 그래프가 보여준다.
          */}
          <Stat
            label={`마진 · ${rangeLabel}`}
            value={won(cashMargin)}
            note="수납액 − 지출"
            warn={cashMargin < 0}
          />
          <Stat
            label="이용 계좌 · 현재"
            value={`${liveAccounts}계좌`}
            note={`월간 ${liveMonthly} · 연간 ${liveAnnual} · 계약사 ${data?.companies.length ?? 0}곳`}
          />
        </div>

        <div className="mt-4 rounded-[10px] border border-[#D9D9D9] p-4">
          <h3 className="mb-1 text-[14px] font-semibold">
            {months === 1 ? "일별 수납액" : "월별 추이"}
          </h3>
          <p className="mb-3 text-[11px] text-[#888]">
            {months === 1 ? (
              <>
                실제로 받은 돈을 날짜별로 쌓았습니다. 연동사 요금은 계좌당
                월정액이라
                <b> 하루 단위로는 지출·마진을 셈하지 않습니다.</b>
              </>
            ) : (
              <>
                실제로 받은 돈을 그립니다. 연간 결제는 받은 달에 통째로 잡혀{" "}
                <b>그달만 크게 튑니다</b> — 어느 달에 연간 계약이 들어왔는지
                색으로 구분됩니다.
              </>
            )}
          </p>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              {months === 1 ? (
                <BarChart
                  data={daily}
                  margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
                >
                  <CartesianGrid stroke="#EEEEEE" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11, fill: "#888" }}
                    tickLine={false}
                    interval={1}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#888" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={shortWon}
                    width={44}
                  />
                  <Tooltip
                    formatter={(value: number) => won(value)}
                    labelFormatter={(day: number) => `${day}일`}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      borderColor: "#E0E0E0",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {/* 같은 자리에 쌓아 그날 들어온 돈의 구성을 보여준다 */}
                  <Bar
                    dataKey="월간"
                    stackId="paid"
                    fill="#4285F4"
                    maxBarSize={18}
                  />
                  <Bar
                    dataKey="연간"
                    stackId="paid"
                    fill="#BBD1FA"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={18}
                  />
                </BarChart>
              ) : (
                <ComposedChart
                  data={chart}
                  margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
                >
                  <CartesianGrid stroke="#EEEEEE" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: "#888" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#888" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={shortWon}
                    width={44}
                  />
                  <Tooltip
                    formatter={(value: number) => won(value)}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      borderColor: "#E0E0E0",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {/* 수납액을 요금제별로 쌓는다. 튄 달의 이유가 색으로 드러난다 */}
                  <Bar
                    dataKey="월간"
                    stackId="paid"
                    fill="#4285F4"
                    maxBarSize={26}
                  />
                  <Bar
                    dataKey="연간"
                    stackId="paid"
                    fill="#9DBEF8"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={26}
                  />
                  <Bar
                    dataKey="지출"
                    fill="#DDDDDD"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={26}
                  />
                  <Line
                    dataKey="마진"
                    stroke="#222222"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-4 rounded-[10px] border border-[#D9D9D9] p-4">
          <h3 className="mb-3 text-[14px] font-semibold">회사별 (이번 달)</h3>
          {loading ? (
            <p className="py-6 text-center text-[12px] text-[#888]">
              불러오는 중입니다…
            </p>
          ) : !data?.companies.length ? (
            <p className="py-6 text-center text-[12px] text-[#888]">
              아직 이용 중인 회사가 없습니다.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left">
                <thead className="bg-[#FAFAFA] text-[11px] text-[#888]">
                  <tr>
                    <Th>회사</Th>
                    <Th>계좌</Th>
                    <Th>월 환산</Th>
                    <Th>이번 달 수납</Th>
                    <Th>상태</Th>
                  </tr>
                </thead>
                <tbody className="text-[13px]">
                  {data.companies.map((row) => (
                    <CompanyRow key={row.companyId} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Pod24AdminLayout>
  );
};

const CompanyRow = ({ row }: { row: AutoConfirmRevenueCompany }) => {
  const [label, tone] = STATUS_LABEL[row.status] ?? [
    row.status,
    "bg-[#F5F5F5] text-[#888888]",
  ];
  const annual = row.plans.ANNUAL ?? 0;
  return (
    <tr className="border-t border-[#EEEEEE]">
      <Td>
        <a
          href={`/admin/company/manage/${row.companyId}`}
          className="font-medium text-[#4285F4] hover:underline"
        >
          {row.companyName ?? `회사 #${row.companyId}`}
        </a>
      </Td>
      <Td className="text-[#555]">
        {row.accounts}계좌
        {annual > 0 && (
          <span className="ml-1 text-[11px] text-[#888]">(연간 {annual})</span>
        )}
      </Td>
      <Td className="text-[#222]">{won(row.monthlyAmount)}</Td>
      {/* 연간 계약은 결제한 달이 아니면 0 이다. 밀린 것과 헷갈리지 않게 상태를 옆에 둔다 */}
      <Td className="text-[#555]">
        {row.paidAmount ? won(row.paidAmount) : "—"}
      </Td>
      <Td>
        <span className={`rounded px-2 py-1 text-[11px] font-medium ${tone}`}>
          {label}
        </span>
      </Td>
    </tr>
  );
};

const Stat = ({
  label,
  value,
  note,
  warn,
}: {
  label: string;
  value: string;
  note: string;
  warn?: boolean;
}) => (
  <div className="rounded-[10px] border border-[#D9D9D9] p-4">
    <div className="text-[12px] text-[#888]">{label}</div>
    <div
      className={`mt-1 text-[20px] font-bold ${warn ? "text-[#4285F4]" : "text-[#222]"}`}
    >
      {value}
    </div>
    <div className="mt-0.5 text-[11px] text-[#888]">{note}</div>
  </div>
);

const Th = ({ children }: { children: React.ReactNode }) => (
  <th className="px-3 py-2 font-medium">{children}</th>
);

const Td = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;

export default BankTransferRevenue;
